import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, useConnectionState } from '../lib/hooks';
import { authApi, communityApi, gdprApi } from '../lib/domain';
import { authStore } from '../lib/authStore';
import { db, type OutboxEntry } from '../lib/db';
import { discardFailedOutboxEntry, retryOutboxEntry } from '../lib/offline';
import { defaultScopeForUser } from '../lib/scopeStore';
import { ApiError } from '../lib/api';
import { onlineStore } from '../lib/onlineStore';
import { PushToggle } from '../components/PushToggle';

export function SettingsPage() {
  const { user } = useAuth();
  const { forcedOffline, degraded, browserOnline } = useConnectionState();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<Array<{ id: string; userAgent: string | null; createdAt: string }>>([]);
  const [currentSessionId, setCurrentSessionId] = useState('');
  const [outbox, setOutbox] = useState<OutboxEntry[]>([]);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [pwMessage, setPwMessage] = useState<string | null>(null);
  const [usernameInput, setUsernameInput] = useState('');
  const [usernameMessage, setUsernameMessage] = useState<string | null>(null);

  useEffect(() => {
    authApi.sessions().then((r) => {
      setSessions(r.sessions);
      setCurrentSessionId(r.currentSessionId);
    });
    db.outbox.toArray().then(setOutbox);
  }, []);

  async function toggleCommunity() {
    if (!user) return;
    await communityApi.setOptIn(defaultScopeForUser(user.id), !user.communityOptIn);
    const me = await authApi.me();
    authStore.setUser(me.user);
  }

  async function saveUsername(e: FormEvent) {
    e.preventDefault();
    setUsernameMessage(null);
    try {
      const res = await authApi.setUsername(usernameInput.trim());
      authStore.setUser(res.user);
      setUsernameMessage('Benutzername gespeichert.');
    } catch (err) {
      setUsernameMessage(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.');
    }
  }

  async function changePassword(e: FormEvent) {
    e.preventDefault();
    setPwMessage(null);
    try {
      await authApi.changePassword(currentPassword, newPassword);
      setPwMessage('Passwort geändert. Andere Sitzungen wurden abgemeldet.');
      setCurrentPassword('');
      setNewPassword('');
    } catch (err) {
      setPwMessage(err instanceof ApiError ? err.message : 'Fehler beim Ändern.');
    }
  }

  async function exportData() {
    const data = await gdprApi.exportData();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'export.json';
    a.click();
    URL.revokeObjectURL(url);
  }

  async function deleteAccount() {
    if (!confirm('Konto wirklich unwiderruflich löschen?')) return;
    try {
      await gdprApi.deleteAccount();
      navigate('/login');
    } catch (err) {
      alert(err instanceof ApiError ? err.message : 'Löschung nicht möglich.');
    }
  }

  return (
    <div className="stack">
      <h1>Einstellungen</h1>

      <div className="card stack">
        <h2>Verbindung</h2>
        <p className="muted">
          Bei schlechtem Empfang (z. B. im Geschäft) erkennt die App eine ausbleibende Antwort automatisch und verhält sich dann wie
          im Offline-Modus - Änderungen werden lokal gespeichert und später synchronisiert.
          {degraded && !forcedOffline && browserOnline && ' Gerade wurde genau das erkannt (schwache Verbindung).'}
        </p>
        <label className="row between">
          <span>Manuell offline arbeiten</span>
          <input type="checkbox" checked={forcedOffline} onChange={(e) => onlineStore.setForcedOffline(e.target.checked)} />
        </label>
        <p className="muted">
          Erzwingt den Offline-Modus, ohne die Internetverbindung des Geräts zu trennen - z. B. um bei bekannt schlechtem Empfang
          nicht auf Timeouts warten zu müssen.
        </p>
      </div>

      <div className="card row between">
        <span>Community-Freigabe (privat)</span>
        <label className="row">
          <input type="checkbox" checked={user?.communityOptIn ?? false} onChange={toggleCommunity} />
          {user?.communityOptIn ? 'an' : 'aus'}
        </label>
      </div>
      <p className="muted">
        Standardmäßig aus (COM-001). Bei Freigabe werden nur Produkt, Filiale, Preis, Menge/Grundpreis, Datum und Aktionskennzeichnung anonym
        bereitgestellt - niemals Name, Gruppenname, E-Mail oder Wohnadresse.
      </p>

      <div className="card stack">
        <h2>Benachrichtigungen</h2>
        <PushToggle />
        <Link to="/notifications">Alle Benachrichtigungen anzeigen</Link>
      </div>

      <div className="card stack">
        <h2>Verwaltung</h2>
        <Link to="/guide">Erste Schritte (Anleitung)</Link>
        <Link to="/categories">Kategorien verwalten</Link>
        <Link to="/stores">Geschäfte & Filialen</Link>
        <Link to="/purchases">Einkaufshistorie</Link>
        {user?.isSystemAdmin && <Link to="/admin">Administration</Link>}
      </div>

      <div className="card stack">
        <h2>Benutzername</h2>
        {user?.username ? (
          <p>
            <strong>{user.username}</strong> <span className="muted">(kann nur von einem Administrator geändert werden)</span>
          </p>
        ) : (
          <form className="stack" onSubmit={saveUsername}>
            <p className="muted">Dein Konto hat noch keinen Benutzernamen. Erlaubt sind 3-32 Zeichen: A-Z, a-z, 0-9, - und _.</p>
            <input
              id="username"
              placeholder="Benutzername"
              required
              minLength={3}
              maxLength={32}
              pattern="[A-Za-z0-9_\-]+"
              title="Nur Buchstaben (A-Z), Ziffern sowie - und _"
              value={usernameInput}
              onChange={(e) => setUsernameInput(e.target.value)}
            />
            <button className="primary" type="submit">Speichern</button>
          </form>
        )}
        {usernameMessage && <p className="muted" role="status">{usernameMessage}</p>}
      </div>

      <div className="card stack">
        <h2>Passwort ändern</h2>
        <form className="stack" onSubmit={changePassword}>
          <input type="password" placeholder="Aktuelles Passwort" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
          <input type="password" placeholder="Neues Passwort (mind. 6 Zeichen)" minLength={6} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
          <button className="primary" type="submit">
            Ändern
          </button>
          {pwMessage && <p className="muted">{pwMessage}</p>}
        </form>
      </div>

      <div className="card stack">
        <h2>Sitzungen</h2>
        <ul className="list-reset stack">
          {sessions.map((s) => (
            <li key={s.id} className="row between">
              <span>
                {s.userAgent ?? 'Unbekanntes Gerät'} {s.id === currentSessionId && <span className="badge ok">Aktuell</span>}
              </span>
              {s.id !== currentSessionId && (
                <button
                  onClick={async () => {
                    await authApi.revokeSession(s.id);
                    authApi.sessions().then((r) => setSessions(r.sessions));
                  }}
                >
                  Abmelden
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>

      {outbox.length > 0 && (
        <div className="card stack">
          <h2>Offline-Warteschlange (SYNC-007)</h2>
          {outbox.map((o) => (
            <div key={o.id} className="row between">
              <span>
                {o.entityType} {o.status === 'failed' && <span className="badge danger">Fehlgeschlagen: {o.lastError}</span>}
              </span>
              {o.status === 'failed' && (
                <div className="row">
                  <button onClick={() => retryOutboxEntry(o.id).then(() => db.outbox.toArray().then(setOutbox))}>Erneut versuchen</button>
                  <button className="danger" onClick={() => discardFailedOutboxEntry(o.id).then(() => db.outbox.toArray().then(setOutbox))}>
                    Verwerfen
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="card stack">
        <h2>Datenschutz (DSGVO)</h2>
        <button onClick={exportData}>Eigene Daten exportieren</button>
        <button className="danger" onClick={deleteAccount}>
          Konto löschen
        </button>
      </div>

      <button onClick={() => authApi.logout().then(() => navigate('/login'))}>Abmelden</button>
    </div>
  );
}
